import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Filter, Eye } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    scheduled:       { label: "Scheduled",       bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
    in_progress:     { label: "In Progress",     bg: "bg-sky-100",   text: "text-sky-800",   border: "border-sky-200" },
    action_required: { label: "Action Required", bg: "bg-amber-100", text: "text-amber-900", border: "border-amber-300" },
    completed:       { label: "Completed",       bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
    overdue:         { label: "Overdue",         bg: "bg-red-100",   text: "text-red-800",   border: "border-red-200" },
  };
  const s = map[status] || map.scheduled;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>{s.label}</span>;
}

function CqcPills({ items }) {
  if (!items?.length) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((k) => (
        <span key={k} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">{k}</span>
      ))}
    </div>
  );
}

export default function AuditRecordsTab({ search, onOpenAudit, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ site_id: "", category: "", status: "", cqc_key_question: "" });
  const [constants, setConstants] = useState({ categories: [], statuses: [], cqc_key_questions: [] });
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (search?.trim()) params.search = search.trim();
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get("/audits-qa/records", { params });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters, search]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => {
    api.get("/audits-qa/constants").then((r) => setConstants(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, []);

  const activeFilters = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  return (
    <div className="space-y-3" data-testid="audits-qa-records-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 tabular-nums">{rows.length} audit{rows.length === 1 ? "" : "s"}</div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="btn-audits-toggle-filters" className={`h-8 text-xs ${showFilters ? "bg-slate-100" : ""}`}>
          <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters {activeFilters > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-800">{activeFilters}</span>}
        </Button>
      </div>
      {showFilters && (
        <div className="p-3 rounded-lg border border-slate-200 bg-white grid grid-cols-2 md:grid-cols-4 gap-2">
          <FS label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })} options={sites.map((s) => ({ v: s.id, label: s.name }))} testid="filter-audits-site" />
          <FS label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={(constants.categories || []).map((c) => ({ v: c.key, label: c.label }))} testid="filter-audits-category" />
          <FS label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={(constants.statuses || []).map((s) => ({ v: s, label: s.replace(/_/g, " ") }))} testid="filter-audits-status" />
          <FS label="CQC Key Question" value={filters.cqc_key_question} onChange={(v) => setFilters({ ...filters, cqc_key_question: v })} options={(constants.cqc_key_questions || []).map((k) => ({ v: k, label: k }))} testid="filter-audits-cqc" />
          <div className="col-span-2 md:col-span-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setFilters({ site_id: "", category: "", status: "", cqc_key_question: "" })} className="h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? <div className="p-8 text-sm text-slate-500">Loading…</div>
          : rows.length === 0 ? <div className="p-16 text-center text-sm text-slate-500">No audits match the current filters.</div>
          : (
          <div className="overflow-x-auto">
            <table className="data-table w-full" data-testid="audits-records-table">
              <thead>
                <tr>
                  <th>Audit</th><th>Category</th><th>Site</th><th>Date</th><th>Auditor</th>
                  <th>Score</th><th>CQC Mapping</th><th>Findings</th><th>Status</th>
                  <th className="w-20 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const site = sites.find((s) => s.id === r.site_id);
                  return (
                    <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                      <td><button className="text-slate-900 hover:text-sky-700 hover:underline text-left" onClick={() => onOpenAudit?.(r.id)}>{r.title}</button></td>
                      <td className="text-slate-600">{r.category_label}</td>
                      <td className="text-slate-500">{site?.name || <span className="text-slate-300">—</span>}</td>
                      <td className="tabular-nums">{r.conducted_date || "—"}</td>
                      <td>{r.auditor || "—"}</td>
                      <td className="tabular-nums">{r.score_num != null ? `${r.score_num}%` : "—"}</td>
                      <td><CqcPills items={r.cqc_key_questions} /></td>
                      <td>{r.findings_list?.length ? <span className="text-slate-700 text-xs">{r.open_findings} open · {r.findings_list.length} total</span> : <span className="text-slate-300 text-xs">—</span>}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="text-right">
                        <button onClick={() => onOpenAudit?.(r.id)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-open-audit-${r.id}`}><Eye className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FS({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-0.5 w-full h-8 text-sm border border-slate-300 rounded-md bg-white px-2">
        <option value="">All</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
